import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Button,
} from '@mui/material';
import { ChevronDown } from 'lucide-react';
import ContactFormDialog from './ContactFormDialog';
import Reveal from './Reveal';
import { tokens } from '../theme';

const faqs = [
  {
    category: 'General',
    questions: [
      {
        question: 'What is CleoLead?',
        answer:
          'CleoLead is an AI Business Manager that sells, supports, and organizes for you. It works as an AI receptionist answering your phone, a chat AI trained on your business that handles helpdesk and questions, and a sales agent that qualifies and follows up 24/7 — across Instagram, WhatsApp, Telegram, web, SMS, voice, and email. One AI runs your front office.',
      },
      {
        question: 'How is this different from a chatbot or a CRM?',
        answer:
          'Chatbots are rule-based flows on one channel. CRMs sit empty until someone fills them in. CleoLead actually does the work: answers the phone, replies on every channel, qualifies leads, books reservations, handles refunds, and follows up — all in your voice, trained on your documents, website, and real past conversations. Reviewable before anything goes out.',
      },
      {
        question: 'Do I need technical skills?',
        answer:
          'No! Connect your Facebook Page in 2 clicks. The entire setup takes under 5 minutes with no coding required.',
      },
    ],
  },
  {
    category: 'Training & Data',
    questions: [
      {
        question: 'How do I train the AI on my business?',
        answer:
          'Upload documents (PDF, DOC, TXT), add website URLs for automatic crawling, create custom Q&A pairs, and import real conversations from your platforms. The AI learns from all sources to become an expert on your business.',
      },
      {
        question: 'Can the AI learn from my team\'s conversations?',
        answer:
          'Yes! Import real conversations from Facebook, Instagram, or Telegram. The AI learns your communication style and helps you draft responses that match your brand voice.',
      },
      {
        question: 'Is my data secure?',
        answer:
          'We use enterprise-grade encryption, OAuth for secure platform connections, and comply with GDPR and CCPA. Your data is never shared or sold.',
      },
    ],
  },
  {
    category: 'Pricing',
    questions: [
      {
        question: 'Is there a free trial?',
        answer:
          'Yes — every paid plan starts with a 14-day free trial. No credit card required, cancel anytime. Pick Professional, Business, or Enterprise based on the call/SMS volume you expect, and upgrade or downgrade between tiers as you grow.',
      },
      {
        question: 'Can I cancel anytime?',
        answer:
          'Yes, cancel your subscription at any time. No long-term contracts or cancellation fees.',
      },
      {
        question: 'What happens if I exceed my limits?',
        answer:
          'New conversations will be paused until the next billing cycle. You can upgrade immediately, or the AI will let customers know a human will follow up.',
      },
    ],
  },
  {
    category: 'Integrations',
    questions: [
      {
        question: 'Which platforms do you support?',
        answer:
          'Facebook Messenger, Instagram Direct Messages, Telegram, and Website Chat Widget.',
      },
      {
        question: 'Can I export data to my CRM?',
        answer:
          'Yes! API integration to any CRM, Google Sheets sync, SQL database connections, CSV/JSON downloads. Set up auto-triggers to export on form completion.',
      },
      {
        question: 'Google Sheets integration?',
        answer:
          'Yes, auto-export leads to Google Sheets in real-time. Set it up once and your spreadsheet updates automatically.',
      },
    ],
  },
];

export default function FAQSection() {
  const [expanded, setExpanded] = useState<string | false>(false);
  const [contactOpen, setContactOpen] = useState(false);

  const handleChange = (panel: string) => (
    _: React.SyntheticEvent,
    isExpanded: boolean,
  ) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box
      id="faq"
      sx={{
        py: { xs: 8, md: 12 },
        bgcolor: 'background.default',
      }}
    >
      <Container maxWidth="lg">
        <Reveal sx={{ textAlign: 'center', mb: 7 }}>
          <Typography variant="overline" sx={{ display: 'block', mb: 1 }}>
            FAQ
          </Typography>
          <Typography variant="h2" sx={{ mb: 2 }}>
            Frequently Asked Questions
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: 'text.secondary', maxWidth: 480, mx: 'auto' }}
          >
            Everything you need to know about CleoLead
          </Typography>
        </Reveal>

        <Grid container spacing={4}>
          {faqs.map((category, catIndex) => (
            <Grid size={{ xs: 12, md: 6 }} key={catIndex}>
              <Reveal delay={catIndex * 70}>
                <Typography
                  variant="overline"
                  sx={{
                    display: 'block',
                    mb: 2,
                  }}
                >
                  {category.category}
                </Typography>
                {category.questions.map((faq, faqIndex) => {
                  const panelId = `panel-${catIndex}-${faqIndex}`;
                  return (
                    <Accordion
                      key={faqIndex}
                      expanded={expanded === panelId}
                      onChange={handleChange(panelId)}
                      sx={{
                        mb: 1.5,
                        overflow: 'hidden',
                        transition: `border-color ${tokens.motion.micro}ms ease, box-shadow ${tokens.motion.micro}ms ease`,
                        '&:hover': {
                          borderColor: tokens.copper,
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <ChevronDown
                            size={20}
                            strokeWidth={1.5}
                            color={tokens.textSecondary}
                          />
                        }
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.95rem',
                          }}
                        >
                          {faq.question}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {faq.answer}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Reveal>
            </Grid>
          ))}
        </Grid>

        {/* CTA */}
        <Reveal sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
            Still have questions?
          </Typography>
          <Button
            variant="outlined"
            onClick={() => setContactOpen(true)}
          >
            Talk to Us
          </Button>
          <ContactFormDialog
            open={contactOpen}
            onClose={() => setContactOpen(false)}
            title="Talk to Us"
            subtitle="Have a question? Fill out the form and we'll get back to you within 24 hours."
            formType="general"
          />
        </Reveal>
      </Container>
    </Box>
  );
}
